import { setProgressBarCurrentStep } from '@avalara/bat-apps-shared/client/actions/progress-bar';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SIcon } from '@avalara/skylab-sdk/react';

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Pages } from '../../../../../types/offers';
import IframePaymentContainer from '../../../containers/iframe-payment-container';
import { updateProductAnalytics } from '../../../lib/side-effects/analytics';
import { goToNextOfferStep, goToPreviousOfferStep } from '../../../lib/side-effects/offer-flow';
import { stepNames } from '../../../lib/side-effects/progress-bar';
import { selectIsNonAdmin, selectState } from '../../../selectors';
import { isEmbeddedSelector } from '../../../selectors/offer';
import { setCurrentOfferPage } from '../../../store/offer';
import { setNeedToFetchCybersourceData } from '../../../store/system';

import { useAppDispatch } from '../../../hooks/use-app-dispatch';
import { purchaseDeflectionWording } from '../../helpers';
import ExistingPaymentMethodNotice from '../../shared/payment/existing-payment-method-notice';
import PaymentSelection from '../../shared/payment/payment-selection';
import SignInLink from '../sign-in-link';
import { FormContainer } from '../styled/forms';

const PaymentFormContainer = styled(FormContainer)`
  height: inherit;
`;

type Props = {
  isEmbedded?: boolean;
};

const OfferPaymentForm = (props: Props) => {
  const {
    account: { isLoggedIn, paymentMethods, selectedPaymentMethod },
    data: { isLoading },
    features,
    offer: { offerFlow },
  } = useSelector(selectState);

  const dispatch = useAppDispatch();
  const history = useHistory();

  const isNonAdmin = useSelector(selectIsNonAdmin);
  const isHighRadiusEnabled = !!features.ENABLEHIGHRADIUSMIGRATION;
  const showHighRadiusNoticeOnly = isLoggedIn && isHighRadiusEnabled;
  const usingExistingPaymentMethod = !!selectedPaymentMethod;
  const hideIframe = usingExistingPaymentMethod || (isLoggedIn && isNonAdmin) || showHighRadiusNoticeOnly;
  const nextButtonAction =
    usingExistingPaymentMethod || showHighRadiusNoticeOnly ? () => dispatch(goToNextOfferStep({ history })) : undefined;
  const isEmbedded = useSelector(isEmbeddedSelector);

  useEffect(() => {
    if (!hideIframe && !(isLoggedIn && isHighRadiusEnabled)) {
      dispatch(setNeedToFetchCybersourceData(true));
    } else dispatch(setNeedToFetchCybersourceData(false));
  }, [hideIframe, isLoggedIn, isHighRadiusEnabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.Payment));
    dispatch(setProgressBarCurrentStep(stepNames.paymentMethod));
    dispatch(updateProductAnalytics('purchase_start', !!offerFlow));
  }, []);

  if (isLoggedIn && isNonAdmin) {
    return (
      <PaymentFormContainer isEmbedded={isEmbedded} className='pad-all-xl text-center'>
        <p className='text-lg font-semibold'>{purchaseDeflectionWording}</p>
      </PaymentFormContainer>
    );
  }

  const goBack = () => {
    dispatch(goToPreviousOfferStep(history));
  };

  let paymentHeaderTitle = t('client.headings.add-a-credit-card');
  if (showHighRadiusNoticeOnly) {
    paymentHeaderTitle = t('client.headings.payment-method');
  } else if (paymentMethods.length) {
    paymentHeaderTitle = t('client.headings.select-a-payment-method');
  }

  return (
    <PaymentFormContainer id='payment-form-container' isEmbedded={isEmbedded}>
      <h1 data-ref='buy.payment.header'>{paymentHeaderTitle}</h1>

      {!isLoggedIn && <SignInLink />}

      {showHighRadiusNoticeOnly ? (
        <ExistingPaymentMethodNotice />
      ) : (
        <>
          {paymentMethods.length ? <PaymentSelection /> : null}
          {selectedPaymentMethod == null && (
            <IframePaymentContainer hideIframe={isLoading || hideIframe} adjustWidth={true} />
          )}
        </>
      )}

      {props.isEmbedded && (
        <div className='text-right'>
          <button type='button' className='secondary margin-right-lg' onClick={goBack}>
            {t('client.buttons.back')}
          </button>
          <button type='button' className='primary icon-trailing' onClick={nextButtonAction}>
            {t('client.buttons.next')}
            <SIcon name='arrow-circle-right' />
          </button>
        </div>
      )}
    </PaymentFormContainer>
  );
};

export default OfferPaymentForm;
